import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { SkeletonTarjetas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { useCalidad, type Hallazgo } from './api';
import type { IrA } from './Administracion';

/**
 * Calidad de datos — el resultado del escaneo del maestro.
 *
 * ## Lo que cambia
 *
 * - **La gravedad ya no se dice solo con el color.** Eran tres hexadecimales
 *   —`#E74C3C`, `#F5A623`, `#2980B9`— con el tinte armado concatenando alfa
 *   (`${col}1f`), un borde izquierdo de cuatro píxeles del mismo color y la
 *   palabra «Error» en gris pequeño a la derecha. Quien no distingue rojo de
 *   naranja veía tres tarjetas iguales. Ahora la insignia lleva su palabra.
 * - **La puntuación se lee.** `font-size:var(--fs-9)` en un color de semáforo
 *   era el número más grande de la pantalla y el menos legible de los tres
 *   estados.
 * - **Los enlaces para corregir se pueden usar con el teclado.** Eran `<li>` con
 *   un `onclick` y un subrayado puesto a mano en el estilo: parecían enlaces, no
 *   recibían el foco y no se podían abrir en otra pestaña.
 */

/** El plural va escrito, no derivado: «Error» + s da «errors», que es inglés.
 *  Salió en la primera revisión en el navegador, con «0 errors» en pantalla. */
const SEVERIDADES: Record<string, {
  tono: 'danger' | 'warn' | 'info';
  icono: string;
  palabra: string;
  plural: string;
}> = {
  error: { tono: 'danger', icono: 'bi-exclamation-octagon', palabra: 'Error', plural: 'errores' },
  warning: { tono: 'warn', icono: 'bi-exclamation-triangle', palabra: 'Advertencia', plural: 'advertencias' },
  info: { tono: 'info', icono: 'bi-info-circle', palabra: 'Informativo', plural: 'informativos' },
};

export function Calidad({ onIrA }: { onIrA: IrA }) {
  const consulta = useCalidad(true);
  const d = consulta.data;

  if (consulta.isPending) return <SkeletonTarjetas n={4} />;
  if (consulta.isError || !d) {
    return (
      <EstadoError
        err={consulta.error}
        que="la calidad de los datos"
        onReintentar={() => void consulta.refetch()}
      />
    );
  }

  return (
    <>
      <header className="mb-4">
        <h2 className="text-4 font-bold text-brand-ink">Calidad de datos</h2>
        <p className="mt-1 text-3 text-muted">
          Unicidad, fechas válidas, jefe activo, coherencia empresa–sede y cobertura de
          catálogos.
        </p>
      </header>

      <Card className="mb-4 flex flex-wrap items-center gap-5 p-4">
        <p className="text-center">
          <span className="block text-9 font-extrabold leading-none tabular-nums text-brand-ink">
            {d.score}
          </span>
          <span className="text-1 text-muted">Salud del dato</span>
        </p>
        <div className="min-w-48 flex-1">
          <p className="mb-2 text-2 text-muted">
            {d.scanned_users} {d.scanned_users === 1 ? 'colaborador analizado' : 'colaboradores analizados'}
            {' · '}
            {d.total_findings} {d.total_findings === 1 ? 'hallazgo' : 'hallazgos'}
          </p>
          <p className="flex flex-wrap gap-2">
            {(['error', 'warning', 'info'] as const).map((sev) => {
              const s = SEVERIDADES[sev]!;
              const n = d.summary[sev] ?? 0;
              return (
                <Badge key={sev} tone={s.tono}>
                  <i className={`bi ${s.icono}`} aria-hidden="true" /> {n}{' '}
                  {n === 1 ? s.palabra.toLowerCase() : s.plural}
                </Badge>
              );
            })}
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          loading={consulta.isFetching}
          onClick={() => void consulta.refetch()}
        >
          <i className="bi bi-arrow-clockwise" aria-hidden="true" /> Re-escanear
        </Button>
      </Card>

      {!d.findings.length ? (
        <Card className="p-6 text-center">
          <i className="bi bi-check-circle text-8 text-ok-ink" aria-hidden="true" />
          <p className="mt-2 text-3 text-muted">Sin hallazgos. El maestro de datos está sano.</p>
        </Card>
      ) : (
        <ul className="flex flex-col gap-2">
          {d.findings.map((f) => (
            <li key={f.code}>
              <TarjetaDeHallazgo hallazgo={f} onIrA={onIrA} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function TarjetaDeHallazgo({ hallazgo: f, onIrA }: { hallazgo: Hallazgo; onIrA: IrA }) {
  const s = SEVERIDADES[f.severity]
    ?? { tono: 'neutral' as const, icono: 'bi-dot', palabra: f.severity, plural: f.severity };
  const mostrados = f.items ?? [];

  return (
    <Card className="p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={s.tono as 'danger' | 'warn' | 'info'}>
          <i className={`bi ${s.icono}`} aria-hidden="true" /> {s.palabra}
        </Badge>
        <strong className="text-3 text-brand-ink">{f.label}</strong>
        <span className="text-2 tabular-nums text-muted">
          {f.count} {f.count === 1 ? 'caso' : 'casos'}
        </span>
      </div>
      {f.hint && <p className="mt-1 text-2 text-muted">{f.hint}</p>}
      {mostrados.length > 0 && (
        <ul className="mt-2 flex flex-col gap-0.5 text-2">
          {mostrados.map((i) => (
            <li key={i.id}>
              <EnlaceDeCorreccion codigo={f.code} id={i.id} onIrA={onIrA}>
                {i.label || `Colaborador ${i.id}`}
              </EnlaceDeCorreccion>
            </li>
          ))}
          {f.count > mostrados.length && (
            <li className="text-muted">…y {f.count - mostrados.length} más</li>
          )}
        </ul>
      )}
    </Card>
  );
}

/**
 * Ir a corregir el hallazgo.
 *
 * Cada código lleva a un sitio distinto: el jefe de un área se arregla en Áreas,
 * una sede sin empresa en Catálogos, y todo lo demás en la ficha del propio
 * colaborador, que se abre directamente —es lo que hacía el vanilla y es lo
 * único útil: llegar a Usuarios y tener que buscar a mano a quien el informe
 * acaba de nombrar no sirve de nada—.
 */
function EnlaceDeCorreccion({ codigo, id, onIrA, children }: {
  codigo: string;
  id: number;
  onIrA: IrA;
  children: React.ReactNode;
}) {
  const clases = 'text-left text-brand-ink underline hover:no-underline focus-visible:focus-anillo';
  const [seccion, adonde] = codigo === 'jefe_area_invalido' ? ['areas', 'Áreas']
    : codigo === 'sede_sin_empresa' ? ['catalogs', 'Catálogos']
      : ['users', 'su ficha'];

  return (
    <button
      type="button"
      className={clases}
      onClick={() => onIrA(seccion, seccion === 'users' ? id : undefined)}
    >
      {children} <i className="bi bi-arrow-right-short" aria-hidden="true" />
      <span className="sr-only"> — ir a {adonde}</span>
    </button>
  );
}
